<script setup>
import { ref, watch, computed, onMounted } from 'vue';
import { useAuthStore } from '@/stores/auth';
import { useFavoritesStore, bindFavoritesTokenGetter } from '@/stores/favoritesStore';

const auth = useAuthStore();
const favorites = useFavoritesStore();

// Bind token getter on mount
onMounted(() => {
    bindFavoritesTokenGetter(() => auth.token);
});

// Local state
const newFolderName = ref('');
const showNewFolderInput = ref(false);
const editingFolderId = ref(null);
const editingFolderName = ref('');
const formError = ref('');

// Current image ID from auth UI state
const imageId = computed(() => auth.ui.favoritesImageId);

// Load data when modal opens
watch(() => auth.ui.favoritesVisible, async (visible) => {
    if (visible && auth.hasActiveAccess) {
        formError.value = '';
        await favorites.fetchFolders();
        if (imageId.value) {
            await favorites.checkImageFolders(imageId.value);
        }
    }
});

// Close modal
const close = () => {
    auth.hideFavorites();
    resetForm();
};

// Reset form state
const resetForm = () => {
    newFolderName.value = '';
    showNewFolderInput.value = false;
    editingFolderId.value = null;
    editingFolderName.value = '';
    formError.value = '';
};

// Create new folder
const createFolder = async () => {
    formError.value = '';
    const name = newFolderName.value.trim();

    if (!name) {
        formError.value = 'Bitte geben Sie einen Ordnernamen ein.';
        return;
    }

    const result = await favorites.createFolder(name);

    if (result.ok) {
        newFolderName.value = '';
        showNewFolderInput.value = false;
    } else {
        formError.value = result.error || 'Fehler beim Erstellen';
    }
};

// Start editing folder name
const startEdit = (folder) => {
    editingFolderId.value = folder.id;
    editingFolderName.value = folder.name;
};

// Save folder rename
const saveEdit = async () => {
    formError.value = '';
    const name = editingFolderName.value.trim();

    if (!name) {
        formError.value = 'Ordnername darf nicht leer sein.';
        return;
    }

    const result = await favorites.renameFolder(editingFolderId.value, name);

    if (result.ok) {
        editingFolderId.value = null;
        editingFolderName.value = '';
    } else {
        formError.value = result.error || 'Fehler beim Umbenennen';
    }
};

// Cancel editing
const cancelEdit = () => {
    editingFolderId.value = null;
    editingFolderName.value = '';
};

// Delete folder
const deleteFolder = async (folderId) => {
    if (!confirm('Ordner und alle enthaltenen Favoriten löschen?')) return;

    const result = await favorites.deleteFolder(folderId);
    if (!result.ok) {
        formError.value = result.error || 'Fehler beim Löschen';
    }
};

// Toggle favorite in folder
const toggleFavorite = async (folder) => {
    formError.value = '';

    const isInFolder = favorites.isImageInFolder(folder.id);

    if (isInFolder) {
        const result = await favorites.removeFromFolder(folder.id, imageId.value);
        if (!result.ok) {
            formError.value = result.error;
        }
    } else {
        const result = await favorites.addToFolder(folder.id, imageId.value);
        if (!result.ok) {
            formError.value = result.error;
        }
    }
};
</script>

<template>
  <div
    v-if="auth.ui.favoritesVisible"
    class="fixed inset-0 z-[1000] flex items-center justify-center p-4"
    role="dialog"
    aria-modal="true"
  >
    <!-- Backdrop -->
    <div class="absolute inset-0 bg-black/60 backdrop-blur-sm" @click="close" />

    <!-- Modal -->
    <div class="relative bg-orange-50 w-full max-w-md rounded-xl shadow-2xl overflow-hidden max-h-[90vh] flex flex-col">
      <!-- Header -->
      <div class="bg-blue text-white px-6 py-4 flex items-center justify-between shrink-0">
        <h2 class="text-lg font-bold">Zu Favoriten hinzufügen</h2>
        <button
          class="text-white/80 hover:text-white text-2xl leading-none cursor-pointer"
          @click="close"
        >
          &times;
        </button>
      </div>

      <!-- Content -->
      <div class="p-5 overflow-y-auto">
        <!-- Loading state -->
        <div v-if="favorites.loading && !favorites.folders.length" class="text-center py-8">
          <div class="w-8 h-8 border-2 border-orange border-t-transparent rounded-full animate-spin mx-auto"></div>
          <p class="text-blue mt-2">Laden...</p>
        </div>

        <!-- Error message -->
        <p v-if="formError" class="text-red-600 text-sm bg-red-50 border border-red-200 rounded p-2 mb-4">
          {{ formError }}
        </p>

        <!-- Folders list -->
        <div v-if="!favorites.loading || favorites.folders.length" class="space-y-2">
          <!-- No folders message -->
          <p v-if="!favorites.folders.length && !showNewFolderInput" class="text-gray-600 text-center py-4">
            Noch keine Ordner vorhanden.<br>
            Erstellen Sie Ihren ersten Ordner!
          </p>

          <!-- Folder items -->
          <div
            v-for="folder in favorites.folders"
            :key="folder.id"
            class="bg-white rounded-lg border border-gray-200 p-3"
          >
            <!-- View mode -->
            <div v-if="editingFolderId !== folder.id" class="flex items-center gap-3">
              <!-- Checkbox to toggle favorite -->
              <button
                @click="toggleFavorite(folder)"
                class="w-6 h-6 rounded border-2 flex items-center justify-center cursor-pointer transition-colors"
                :class="favorites.isImageInFolder(folder.id)
                  ? 'bg-blue border-blue text-white'
                  : 'border-gray-300 hover:border-blue'"
              >
                <svg v-if="favorites.isImageInFolder(folder.id)" class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 13l4 4L19 7"></path>
                </svg>
              </button>

              <!-- Folder name -->
              <div class="flex-1 min-w-0">
                <span class="text-blue font-medium truncate block">{{ folder.name }}</span>
                <span class="text-gray-500 text-xs">{{ folder.favorite_count || 0 }} Bilder</span>
              </div>

              <!-- Edit/Delete buttons -->
              <div class="flex gap-1 shrink-0">
                <button
                  @click="startEdit(folder)"
                  class="p-1 text-gray-400 hover:text-blue cursor-pointer"
                  title="Umbenennen"
                >
                  <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z"></path>
                  </svg>
                </button>
                <button
                  @click="deleteFolder(folder.id)"
                  class="p-1 text-gray-400 hover:text-red-600 cursor-pointer"
                  title="Löschen"
                >
                  <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"></path>
                  </svg>
                </button>
              </div>
            </div>

            <!-- Edit mode -->
            <div v-else class="flex items-center gap-2">
              <input
                v-model="editingFolderName"
                type="text"
                class="flex-1 border border-gray-300 rounded px-2 py-1 text-sm focus:outline-none focus:ring-1 focus:ring-blue"
                @keyup.enter="saveEdit"
                @keyup.escape="cancelEdit"
              />
              <button
                @click="saveEdit"
                class="p-1 text-green-600 hover:text-green-700 cursor-pointer"
              >
                <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 13l4 4L19 7"></path>
                </svg>
              </button>
              <button
                @click="cancelEdit"
                class="p-1 text-gray-400 hover:text-gray-600 cursor-pointer"
              >
                <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12"></path>
                </svg>
              </button>
            </div>
          </div>

          <!-- New folder input -->
          <div v-if="showNewFolderInput" class="bg-white rounded-lg border-2 border-blue p-3">
            <div class="flex items-center gap-2">
              <input
                v-model="newFolderName"
                type="text"
                placeholder="Neuer Ordnername"
                class="flex-1 border border-gray-300 rounded px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-blue"
                @keyup.enter="createFolder"
                @keyup.escape="showNewFolderInput = false"
                autofocus
              />
              <button
                @click="createFolder"
                :disabled="favorites.loading"
                class="btn-primary-sm"
              >
                Erstellen
              </button>
              <button
                @click="showNewFolderInput = false; newFolderName = ''"
                class="p-2 text-gray-400 hover:text-gray-600 cursor-pointer"
              >
                <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12"></path>
                </svg>
              </button>
            </div>
          </div>

          <!-- Add new folder button -->
          <button
            v-if="!showNewFolderInput"
            @click="showNewFolderInput = true"
            class="w-full py-3 border-2 border-dashed border-gray-300 rounded-lg text-gray-500 hover:border-blue hover:text-blue transition-colors cursor-pointer flex items-center justify-center gap-2"
          >
            <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 4v16m8-8H4"></path>
            </svg>
            Neuen Ordner erstellen
          </button>
        </div>
      </div>

      <!-- Footer -->
      <div class="bg-gray-50 px-5 py-3 border-t border-gray-200 shrink-0">
        <button
          @click="close"
          class="btn-primary w-full"
        >
          Fertig
        </button>
      </div>
    </div>
  </div>
</template>
